/**
 * Single source of truth para los filtros del UI.
 *
 * - `Filters` describe el estado completo (todos opcionales).
 * - `parseUrlFilters()` lee el query string actual y construye un objeto
 *   `Filters` (defensivo: ignora keys desconocidas, valida tipos).
 * - `filtersToParams()` lo invierte para mandárselo al API.
 * - `filtersToUrl()` lo invierte para `history.pushState` — URLs
 *   compartibles (ej: `/?cveEnt=14&propertyType=CASA&q=polanco`).
 *
 * Diseñado pure, sin React, para que sea trivialmente testeable.
 */

import { GRADOS_REZAGO, type GradoRezago } from './types';

/** Orden de lista /v1/properties (whitelist del API). */
export type PropertySort =
  | 'opportunity'
  | 'date_updated'
  | 'date_detected'
  | 'price'
  | 'price_desc'
  | 'discount'
  | 'price_drop';

export const DEFAULT_PROPERTY_SORT: PropertySort = 'opportunity';

export interface Filters {
  q?: string;
  cveEnt?: string;
  /** CHAR(5) — incluye los 2 chars de cve_ent + 3 dígitos de municipio. */
  cveMun?: string;
  legalChannel?: string;
  status?: string;
  propertyType?: string;
  minPrice?: number;
  maxPrice?: number;
  /**
   * Filtra por opportunity_score mínimo (0-100). Calculado por la MV
   * 0014 — combina precio vs zona, crimen YoY, completitud, legal y
   * liquidez. La UI ofrece cortes en 45, 60 y 75, sin adjetivos.
   *
   * Ya no hay filtros «Sin litigio» ni «Libre gravamen» (2026-10-08):
   * TerraKairos no verifica situación jurídica. Un enlace viejo con
   * `excludeLitigio`/`onlyLibreGravamen` se ignora al leer la URL.
   */
  minOpportunityScore?: number;
  /** Descuento mínimo real vs `avaluo_price` (%). */
  minDiscountVsAvaluo?: number;
  /**
   * Nivel socioeconómico de la AGEB (grado de rezago social CONEVAL,
   * RFC-0022 Fase 2). OJO: "Muy bajo" rezago = nivel ALTO. Sólo aplica a
   * propiedades con precisión STREET/COLONIA; el API excluye el resto.
   */
  gradoRezago?: GradoRezago;
  /** Orden del listado; omitido → `DEFAULT_PROPERTY_SORT` en API. */
  sort?: PropertySort;
}

export const EMPTY_FILTERS: Filters = {};

/** True si `f` no tiene ningún filtro aplicado. */
const SORT_WHITELIST = new Set<PropertySort>([
  'opportunity',
  'date_updated',
  'date_detected',
  'price',
  'price_desc',
  'discount',
  'price_drop',
]);

export function isEmpty(f: Filters): boolean {
  return (
    !f.q && !f.cveEnt && !f.cveMun && !f.legalChannel && !f.status && !f.propertyType &&
    f.minPrice === undefined && f.maxPrice === undefined &&
    f.minOpportunityScore === undefined &&
    f.minDiscountVsAvaluo === undefined &&
    !f.gradoRezago &&
    (f.sort === undefined || f.sort === DEFAULT_PROPERTY_SORT)
  );
}

/**
 * Helper defensivo: rechaza strings vacías Y los literales "undefined"/"null"
 * que pueden aparecer en URLs si algo se serializa mal upstream.
 */
function safeStr(v: string | null | undefined): string | null {
  if (!v) return null;
  const t = v.trim();
  if (!t || t === 'undefined' || t === 'null') return null;
  return t;
}

export function parseUrlFilters(search: string = window.location.search): Filters {
  const p = new URLSearchParams(search);
  const out: Filters = {};
  const q = safeStr(p.get('q'));
  if (q) out.q = q.slice(0, 100);
  const cveEnt = safeStr(p.get('cveEnt'));
  if (cveEnt && /^[0-9]{2}$/.test(cveEnt)) out.cveEnt = cveEnt;
  const cveMun = safeStr(p.get('cveMun'));
  if (cveMun && /^[0-9]{5}$/.test(cveMun)) out.cveMun = cveMun;
  const ch = safeStr(p.get('legalChannel'));
  if (ch) out.legalChannel = ch;
  const st = safeStr(p.get('status'));
  if (st) out.status = st;
  const pt = safeStr(p.get('propertyType'));
  if (pt) out.propertyType = pt;
  const minP = Number(p.get('minPrice'));
  if (Number.isFinite(minP) && minP > 0) out.minPrice = minP;
  const maxP = Number(p.get('maxPrice'));
  if (Number.isFinite(maxP) && maxP > 0) out.maxPrice = maxP;
  const minOpp = Number(p.get('minOpportunityScore'));
  if (Number.isFinite(minOpp) && minOpp > 0 && minOpp <= 100) {
    out.minOpportunityScore = minOpp;
  }
  const minDisc = Number(p.get('minDiscountVsAvaluo'));
  if (Number.isFinite(minDisc) && minDisc > 0 && minDisc <= 100) {
    out.minDiscountVsAvaluo = minDisc;
  }
  const grado = safeStr(p.get('gradoRezago'));
  if (grado && (GRADOS_REZAGO as readonly string[]).includes(grado)) {
    out.gradoRezago = grado as GradoRezago;
  }
  const sort = safeStr(p.get('sort'));
  if (sort && SORT_WHITELIST.has(sort as PropertySort)) {
    out.sort = sort as PropertySort;
  }
  return out;
}

/** Construye los `URLSearchParams` que el API REST espera. */
export function filtersToParams(f: Filters): Record<string, string> {
  const out: Record<string, string> = {};
  if (f.q) out.q = f.q;
  if (f.cveEnt) out.cve_ent = f.cveEnt;
  if (f.cveMun) out.cve_mun = f.cveMun;
  if (f.legalChannel) out.legal_channel = f.legalChannel;
  if (f.status) out.status = f.status;
  if (f.propertyType) out.property_type = f.propertyType;
  if (f.minPrice !== undefined) out.min_price = String(f.minPrice);
  if (f.maxPrice !== undefined) out.max_price = String(f.maxPrice);
  if (f.minOpportunityScore !== undefined) {
    out.min_opportunity_score = String(f.minOpportunityScore);
  }
  if (f.minDiscountVsAvaluo !== undefined) {
    out.min_discount_vs_avaluo = String(f.minDiscountVsAvaluo);
  }
  if (f.gradoRezago) out.grado_rezago = f.gradoRezago;
  out.sort = f.sort ?? DEFAULT_PROPERTY_SORT;
  return out;
}

/**
 * Construye la query-string para el browser (sin el "?").
 *
 * Defensivo: solo escribe si el valor es un string no-vacío real (no
 * undefined, no null, no string "undefined"/"null"). Esto evita que un
 * spread `{ ...f, q: undefined }` termine pusheando `?q=undefined`.
 */
export function filtersToUrl(f: Filters): string {
  const p = new URLSearchParams();
  const set = (key: string, val: string | undefined) => {
    if (typeof val !== 'string') return;
    const t = val.trim();
    if (!t || t === 'undefined' || t === 'null') return;
    p.set(key, t);
  };
  set('q', f.q);
  set('cveEnt', f.cveEnt);
  set('cveMun', f.cveMun);
  set('legalChannel', f.legalChannel);
  set('status', f.status);
  set('propertyType', f.propertyType);
  if (typeof f.minPrice === 'number' && Number.isFinite(f.minPrice) && f.minPrice > 0) {
    p.set('minPrice', String(f.minPrice));
  }
  if (typeof f.maxPrice === 'number' && Number.isFinite(f.maxPrice) && f.maxPrice > 0) {
    p.set('maxPrice', String(f.maxPrice));
  }
  if (
    typeof f.minOpportunityScore === 'number' &&
    Number.isFinite(f.minOpportunityScore) &&
    f.minOpportunityScore > 0
  ) {
    p.set('minOpportunityScore', String(f.minOpportunityScore));
  }
  if (
    typeof f.minDiscountVsAvaluo === 'number' &&
    Number.isFinite(f.minDiscountVsAvaluo) &&
    f.minDiscountVsAvaluo > 0
  ) {
    p.set('minDiscountVsAvaluo', String(f.minDiscountVsAvaluo));
  }
  set('gradoRezago', f.gradoRezago);
  const sort = f.sort ?? DEFAULT_PROPERTY_SORT;
  // Solo serializar sort cuando difiere del default: antes toda URL con
  // cualquier filtro cargaba un `&sort=opportunity` redundante que además
  // hacía que el round-trip por popstate produjera un objeto con una key
  // extra (nueva identidad → cascada de refetches).
  if (sort !== DEFAULT_PROPERTY_SORT) {
    p.set('sort', sort);
  }
  return p.toString();
}

/**
 * Lee el deep-link de propiedad (`?property=ID`) de la URL. Validación
 * laxa pero suficiente: IDs alfanuméricos/guiones (UUIDs y source keys),
 * cap de longitud para descartar basura.
 */
export function parseUrlPropertyId(search: string = window.location.search): string | null {
  const v = safeStr(new URLSearchParams(search).get('property'));
  if (!v || v.length > 64 || !/^[A-Za-z0-9_-]+$/.test(v)) return null;
  return v;
}

/**
 * Empuja el nuevo estado a la URL sin recargar la página.
 * Listener `popstate` en App.tsx revierte si el usuario usa back/forward.
 *
 * `propertyId` viaja como `?property=ID` junto a los filtros, así
 * "Copiar enlace" (que copia location.href) comparte también la
 * propiedad abierta en el drawer.
 */
export function pushFiltersToUrl(
  f: Filters,
  propertyId?: string | null,
  mode: 'push' | 'replace' = 'push',
): void {
  const p = new URLSearchParams(filtersToUrl(f));
  if (propertyId) p.set('property', propertyId);
  const qs = p.toString();
  const url = qs ? `?${qs}` : window.location.pathname;
  // 'replace' para ajustes de filtros (cada tecleo/select apilaba una
  // entrada y el botón Atrás requería N clicks para salir de la página);
  // 'push' queda reservado para navegación real (abrir/cerrar propiedad).
  if (mode === 'replace') window.history.replaceState(null, '', url);
  else window.history.pushState(null, '', url);
}

/**
 * Mapping cve_ent (INEGI) → nombre humano. Espejo de
 * `src/contracts/MexicanStates.ts` del backend; lo copiamos acá para
 * evitar tener que servirlo desde el API en cada page-load.
 */
export const ESTADO_NAMES: Record<string, string> = {
  '01': 'Aguascalientes',
  '02': 'Baja California',
  '03': 'Baja California Sur',
  '04': 'Campeche',
  '05': 'Coahuila',
  '06': 'Colima',
  '07': 'Chiapas',
  '08': 'Chihuahua',
  '09': 'Ciudad de México',
  '10': 'Durango',
  '11': 'Guanajuato',
  '12': 'Guerrero',
  '13': 'Hidalgo',
  '14': 'Jalisco',
  '15': 'Estado de México',
  '16': 'Michoacán',
  '17': 'Morelos',
  '18': 'Nayarit',
  '19': 'Nuevo León',
  '20': 'Oaxaca',
  '21': 'Puebla',
  '22': 'Querétaro',
  '23': 'Quintana Roo',
  '24': 'San Luis Potosí',
  '25': 'Sinaloa',
  '26': 'Sonora',
  '27': 'Tabasco',
  '28': 'Tamaulipas',
  '29': 'Tlaxcala',
  '30': 'Veracruz',
  '31': 'Yucatán',
  '32': 'Zacatecas',
};

/** Pretty-print de los enums del backend. Centralizado acá para consistencia. */
export const HUMAN_LABELS = {
  channel: {
    SUBASTA_INDEP: 'INDEP (Subasta)',
    SUBASTA_SAT: 'SAT (Subasta)',
    SUBASTA_INDAABIN: 'INDAABIN',
    VENTA_INDAABIN: 'INDAABIN (Venta)',
    VENTA_SHF: 'SHF (Cartera)',
    REMATE_JUDICIAL: 'Remate judicial',
    RECUPERADA_INFONAVIT: 'Infonavit (Recuperada)',
    RECUPERADA_FOVISSSTE: 'Fovissste (Recuperada)',
    USUCAPION_CANDIDATE: 'Candidata a usucapión',
    PREDIAL_DELINQUENT: 'Adeudo predial',
    BIEN_VACANTE: 'Bien vacante',
    ADJUDICADO_BANCO: 'Banco (Adjudicado)',
    DESCONOCIDO: 'Desconocido',
  } as Record<string, string>,
  status: {
    AVAILABLE: 'Disponible',
    AUCTION_SCHEDULED: 'Subasta programada',
    AUCTION_ACTIVE: 'Subasta en curso',
    UNDER_OFFER: 'En oferta',
    WITHDRAWN: 'Retirada',
    RELISTED: 'Reingresó',
    SOLD_CONFIRMED: 'Vendida (confirmado)',
    PENDING: 'Pendiente',
    SOLD: 'Vendido',
    CANDIDATE_ONLY: 'Catálogo (no listada)',
    DELISTED_UNCONFIRMED: 'Dejó de observarse',
    UNKNOWN: 'Desconocido',
  } as Record<string, string>,
  propertyType: {
    CASA: 'Casa',
    DEPARTAMENTO: 'Departamento',
    TERRENO: 'Terreno',
    LOCAL: 'Local',
    BODEGA: 'Bodega',
    INDUSTRIAL: 'Industrial',
    OFICINA: 'Oficina',
    COMERCIAL: 'Comercial',
    RANCHO: 'Rancho',
    MIXTO: 'Mixto',
    OTRO: 'Otro',
    DESCONOCIDO: 'Desconocido',
  } as Record<string, string>,
};

/**
 * Explicaciones largas por estatus (RFC-0021 A7) — tooltip en ficha y
 * tarjetas. El 79% del inventario es CANDIDATE_ONLY y la etiqueta sola
 * no dice qué puede hacer el usuario con eso. Texto placeholder validable
 * por JAD.
 */
export const STATUS_EXPLANATIONS: Record<string, string> = {
  // Texto final validado por JAD (brief 2026-08-15 §3.2) — fijo, no editar
  // sin su visto bueno.
  CANDIDATE_ONLY:
    'Detectada en el catálogo de la fuente pero sin publicación activa de venta. ' +
    'Útil para mapear inventario y anticipar salidas; no está disponible para oferta directa hoy.',
  DELISTED_UNCONFIRMED:
    'La fuente dejó de devolver este registro en sus últimas corridas. ' +
    'No significa vendida: significa que dejó de observarse — puede reaparecer.',
};

export function humanize(kind: keyof typeof HUMAN_LABELS, key: string): string {
  return HUMAN_LABELS[kind][key] ?? key;
}
